import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BrandHeader } from '../components/BrandHeader';
import { LanguageButtons } from '../components/LanguageButtons';
import { SurveyButton } from '../components/SurveyButton';
import type { Language } from '../content/content';
import { content, languages, tourRoutes } from '../content/content';
import { getStoredLanguage, isLanguage, setStoredLanguage } from '../utils/language';

export function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryLanguage = searchParams.get('lang');
  const initialLanguage = isLanguage(queryLanguage) ? queryLanguage : 'es';
  const [visibleLanguage, setVisibleLanguage] = useState<Language>(initialLanguage);
  const [storedLanguage, setStoredLanguageState] = useState<Language | null>(null);
  const copy = content[visibleLanguage];

  useEffect(() => {
    setStoredLanguageState(getStoredLanguage());
  }, []);

  const alternateLanguage = useMemo<Language>(
    () => (visibleLanguage === 'es' ? 'en' : 'es'),
    [visibleLanguage],
  );

  function startTour(language: Language) {
    setStoredLanguage(language);
    navigate(tourRoutes[language]);
  }

  return (
    <main className="landingPage" lang={visibleLanguage}>
      <div className="backgroundArt" aria-hidden="true" />
      <BrandHeader language={visibleLanguage} />

      <section className="heroSection" aria-labelledby="welcome-title">
        <p className="eyebrow">{copy.durationLabel}</p>
        <h1 id="welcome-title">{copy.welcomeTitle}</h1>
        <p className="heroCopy">{copy.welcomeDescription}</p>
        <p className="supportText">{copy.supportText}</p>

        {storedLanguage ? (
          <div className="savedLanguage" role="status">
            <span>
              {copy.savedLanguage}: {languages[storedLanguage]}
            </span>
            <button type="button" onClick={() => startTour(storedLanguage)}>
              {content[storedLanguage].useSavedLanguage}
            </button>
          </div>
        ) : null}

        <div className="languagePanel">
          <h2>{copy.languageQuestion}</h2>
          <LanguageButtons onSelect={startTour} />
          <button
            className="textButton"
            type="button"
            aria-label={
              visibleLanguage === 'es' ? 'Ver bienvenida en English' : 'View welcome in Español'
            }
            onClick={() => setVisibleLanguage(alternateLanguage)}
          >
            {languages[alternateLanguage]}
          </button>
        </div>

        <aside className="landingSurvey">
          <p>{copy.landingSurveyPrompt}</p>
          <SurveyButton language={visibleLanguage} className="secondaryButton" />
          <small>{copy.surveySmallText}</small>
        </aside>
      </section>
    </main>
  );
}
